import { useEffect, useMemo, useState } from "react";
import "./templateRewardFilters.scss";
import {
  CampaignType,
  Offer,
  OfferCategory,
  TemplateOptionSettings,
} from "../../../../../types/global.types";

export interface Props {
  settings: TemplateOptionSettings | undefined;
  campaign: CampaignType;
  setFilteredList: React.Dispatch<React.SetStateAction<Offer[]>>;
  limitPopupHeight?: boolean;
}

const getVisibleOffers = (offers: Offer[]) =>
  offers.filter((o) => !o.tagBlocked?.isBlocked);

const getOfferIdsKey = (offers: Offer[]) =>
  offers
    .map((o) => o.id)
    .sort()
    .join(",");

const TemplateRewardFilters = ({
  campaign,
  settings,
  setFilteredList,
  limitPopupHeight = false,
}: Props) => {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [offerHostType, setOfferHostType] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const visibleOffers = useMemo(
    () => getVisibleOffers(campaign.offers),
    [campaign.offers]
  );

  const offerIdsKey = useMemo(
    () => getOfferIdsKey(campaign.offers),
    [campaign.offers]
  );

  useEffect(() => {
    setOfferHostType("all");
    setSelectedCategory("");
  }, [offerIdsKey]);

  const hostFilteredOffers = useMemo(() => {
    return visibleOffers.filter((o) => {
      if (offerHostType === "personal") return o.offerSettings.isB2C;
      if (offerHostType === "trade") return !o.offerSettings.isB2C;
      return true;
    });
  }, [visibleOffers, offerHostType]);

  const offerFilteredList = useMemo(() => {
    if (!selectedCategory) return hostFilteredOffers;

    return hostFilteredOffers.filter((o) =>
      o.categories.some((cat: OfferCategory) => cat.name === selectedCategory)
    );
  }, [hostFilteredOffers, selectedCategory]);

  useEffect(() => {
    setFilteredList(offerFilteredList);
  }, [offerFilteredList, setFilteredList]);

  const handleSetFiltersDefault = () => {
    setOfferHostType("all");
    setSelectedCategory("");
    setShowFilters(false);
  };

  const handleHostTypeChange = (type: string) => {
    setOfferHostType(type);
    setSelectedCategory("");
  };

  const offersCategories = useMemo(() => {
    const cats: string[] = [];

    hostFilteredOffers.forEach((o) => {
      o.categories.forEach((cat: OfferCategory) => {
        !cats.includes(cat.name) && cats.push(cat.name);
      });
    });

    return cats;
  }, [hostFilteredOffers]);

  return (
    <div className="reward-filters">
      <div
        className="reward-filters-trigger"
        onClick={() => setShowFilters((prev) => !prev)}
        style={{
          borderColor: settings?.filters?.borderColor || "#939393",
          backgroundColor: settings?.filters?.backgroundColor || "#e5eef5",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.9999 1.33301H2.99992C2.07944 1.33301 1.31187 2.09077 1.53896 2.9828C2.02995 4.91144 3.36178 6.50421 5.12113 7.34779C5.80685 7.67658 6.33325 8.32119 6.33325 9.08166V14.1513C6.33325 14.7708 6.98518 15.1737 7.53926 14.8967L9.20593 14.0633C9.48825 13.9222 9.66658 13.6336 9.66658 13.318V9.08166C9.66658 8.32119 10.193 7.67658 10.8787 7.34779C12.6381 6.50421 13.9699 4.91144 14.4609 2.9828C14.688 2.09077 13.9204 1.33301 12.9999 1.33301Z"
            stroke={settings?.filters?.textColor || "#939393"}
            strokeWidth="1.33333"
          />
        </svg>
        <span style={{ color: settings?.filters?.textColor || "#939393" }}>
          {settings?.filters?.filterOffers || "Filter rewards"}
        </span>
      </div>

      {showFilters && (
        <>
          <div
            className="reward-filters-backdrop"
            onMouseDown={() => setShowFilters(false)}
            aria-hidden="true"
          />
          <div
            className={`reward-filters-popup${limitPopupHeight ? " reward-filters-popup--limited" : ""}`}
            style={{
              backgroundColor: settings?.filters?.backgroundColor || "#ffffff",
            }}
          >
            {!!visibleOffers.find((o) => !o.offerSettings.isB2C) &&
              !!visibleOffers.find((o) => o.offerSettings.isB2C) && (
                <div
                  className="reward-filters-list"
                  style={{
                    borderColor: settings?.filters?.borderColor || "#939393",
                  }}
                >
                  <div
                    className="reward-filters-title"
                    style={{
                      color: settings?.filters?.textColor || "#939393",
                    }}
                  >
                    Type
                  </div>
                  <ul
                    style={{
                      color: settings?.filters?.textColor || "#939393",
                    }}
                  >
                    <li onClick={() => handleHostTypeChange("all")}>
                      <span
                        className="circle"
                        style={{
                          border:
                            offerHostType === "all"
                              ? `3px solid ${settings?.filters?.activeColor || "#0053a0"}`
                              : `1px solid ${settings?.filters?.borderColor || "#939393"}`,
                        }}
                      ></span>
                      All
                    </li>
                    <li onClick={() => handleHostTypeChange("personal")}>
                      <span
                        className="circle"
                        style={{
                          border:
                            offerHostType === "personal"
                              ? `3px solid ${settings?.filters?.activeColor || "#0053a0"}`
                              : `1px solid ${settings?.filters?.borderColor || "#939393"}`,
                        }}
                      ></span>
                      Personal rewards
                    </li>
                    <li onClick={() => handleHostTypeChange("trade")}>
                      <span
                        className="circle"
                        style={{
                          border:
                            offerHostType === "trade"
                              ? `3px solid ${settings?.filters?.activeColor || "#0053a0"}`
                              : `1px solid ${settings?.filters?.borderColor || "#939393"}`,
                        }}
                      ></span>
                      Trade rewards
                    </li>
                  </ul>
                </div>
              )}
            <div
              className="reward-filters-list"
              style={{
                borderColor: settings?.filters?.borderColor || "#939393",
              }}
            >
              <div
                className="reward-filters-title"
                style={{
                  color: settings?.filters?.textColor || "#939393",
                }}
              >
                Industries
              </div>
              <ul
                style={{
                  color: settings?.filters?.textColor || "#939393",
                }}
              >
                {offersCategories.length > 0 && (
                  <li key="all-categories" onClick={() => setSelectedCategory("")}>
                    <span
                      className="circle"
                      style={{
                        border: !selectedCategory
                          ? `3px solid ${settings?.filters?.activeColor || "#0053a0"}`
                          : `1px solid ${settings?.filters?.borderColor || "#939393"}`,
                      }}
                    ></span>
                    All
                  </li>
                )}
                {offersCategories.map((category) => (
                  <li
                    key={category}
                    onClick={() =>
                      setSelectedCategory(
                        category === selectedCategory ? "" : category
                      )
                    }
                  >
                    <span
                      className="circle"
                      style={{
                        border:
                          category === selectedCategory
                            ? `3px solid ${settings?.filters?.activeColor || "#0053a0"}`
                            : `1px solid ${settings?.filters?.borderColor || "#939393"}`,
                      }}
                    ></span>
                    {category}
                  </li>
                ))}
              </ul>
            </div>

            <div className="reward-filters-buttons">
              <div
                className="reward-filters-button"
                style={{
                  backgroundColor:
                    (settings?.filters?.activeColor || "#0053a0") + "20",
                  borderColor: settings?.filters?.activeColor || "#0053a0",
                  color: settings?.filters?.activeColor || "#0053a0",
                }}
                onClick={handleSetFiltersDefault}
              >
                Cancel
              </div>
              <div
                className="reward-filters-button"
                style={{
                  backgroundColor: settings?.filters?.activeColor || "#0053a0",
                  borderColor: settings?.filters?.activeColor || "#0053a0",
                  color: settings?.filters?.backgroundColor || "#ffffff",
                }}
                onClick={() => setShowFilters(false)}
              >
                Apply
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateRewardFilters;
